import { useState, useEffect, useContext } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Chip, Sheet, CircularProgress,
    Card, CardContent, Input, Checkbox, Divider, Tabs, TabList, Tab, TabPanel, Alert
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { LayoutContext } from '../context';
import { APIGetClinicalCategories, APIGenerateClinicalTest, APIDeleteClinicalCase,
    APIQgenListBatches, APIQgenBatchDetail, APIQgenDeleteBatch,
    APIQgenApproveQuestion, APIQgenRejectQuestion, APIQgenApproveAll,
    APIQgenImport, APIQgenStats } from '../api';
import { Play, Plus, Hash, CaretDown, CaretUp, CaretRight, CheckCircle, XCircle, Trash, DownloadSimple, Checks, ArrowClockwise } from "@phosphor-icons/react";
import ClinicalCaseModal from '../components/modals/ClinicalCaseModal';


/* ═══════════════════════════════════════
   Clinical Cases — tree components
   ═══════════════════════════════════════ */

function SystemNode({ system, selectedCats, onToggleCat, onToggleSystem }) {
    const [expanded, setExpanded] = useState(true);

    const allCatIds = system.subsections.flatMap(s => s.categories.map(c => c.id));
    const selectedCount = allCatIds.filter(id => selectedCats.includes(id)).length;
    const isAllSelected = allCatIds.length > 0 && selectedCount === allCatIds.length;
    const isPartial = selectedCount > 0 && !isAllSelected;

    return (
        <Box sx={{ mb: 1.5 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', py: 0.75, px: 0.5,
                    borderRadius: 'sm', cursor: 'pointer',
                    '&:hover': { bgcolor: 'background.level1' },
                }}
            >
                <IconButton size="sm" variant="plain" sx={{ mr: 0.5, minWidth: 28, minHeight: 28 }}>
                    {expanded ? <CaretDown size={20} /> : <CaretRight size={20} />}
                </IconButton>
                <Checkbox
                    checked={isAllSelected} indeterminate={isPartial}
                    onChange={() => onToggleSystem(allCatIds)} size="sm"
                    sx={{ mr: 1, pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                />
                <Typography level="title-sm" sx={{ flex: 1, fontWeight: 600 }}>
                    {system.name}
                </Typography>
                <Chip size="sm" variant="soft" color={selectedCount > 0 ? 'primary' : 'neutral'}>
                    {system.total_questions}
                </Chip>
            </Box>
            {expanded && system.subsections.map(sub => (
                <SubsectionNode
                    key={sub.name} subsection={sub} selectedCats={selectedCats}
                    onToggleCat={onToggleCat} onToggleSubsection={(ids) => onToggleSystem(ids)}
                />
            ))}
        </Box>
    );
}

function SubsectionNode({ subsection, selectedCats, onToggleCat, onToggleSubsection }) {
    const [expanded, setExpanded] = useState(false);

    const catIds = subsection.categories.map(c => c.id);
    const selectedCount = catIds.filter(id => selectedCats.includes(id)).length;
    const isAllSelected = catIds.length > 0 && selectedCount === catIds.length;
    const isPartial = selectedCount > 0 && !isAllSelected;

    return (
        <Box sx={{ ml: 3 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', py: 0.5, px: 0.5,
                    borderRadius: 'sm', cursor: 'pointer',
                    '&:hover': { bgcolor: 'background.level1' },
                }}
            >
                <IconButton size="sm" variant="plain" sx={{ mr: 0.5, minWidth: 24, minHeight: 24 }}>
                    {expanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
                </IconButton>
                <Checkbox
                    checked={isAllSelected} indeterminate={isPartial}
                    onChange={() => onToggleSubsection(catIds)} size="sm"
                    sx={{ mr: 1, pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                />
                <Typography level="body-sm" sx={{ flex: 1, fontWeight: 500, color: 'text.secondary' }}>
                    {subsection.name}
                </Typography>
                <Chip size="sm" variant="plain" color="neutral" sx={{ fontSize: '11px' }}>
                    {subsection.total_questions}
                </Chip>
            </Box>
            {expanded && subsection.categories.map(cat => {
                const isSelected = selectedCats.includes(cat.id);
                return (
                    <Box
                        key={cat.id} onClick={() => onToggleCat(cat.id)}
                        sx={{
                            display: 'flex', alignItems: 'center', py: 0.4, px: 0.5, ml: 4,
                            borderRadius: 'sm', cursor: 'pointer',
                            '&:hover': { bgcolor: 'background.level1' },
                        }}
                    >
                        <Checkbox checked={isSelected} onChange={() => onToggleCat(cat.id)} size="sm"
                            sx={{ mr: 1, pointerEvents: 'none' }} />
                        <Typography level="body-xs"
                            sx={{ flex: 1, color: isSelected ? 'text.primary' : 'text.tertiary' }}>
                            {cat.name}
                        </Typography>
                        <Chip size="sm" variant="soft" color={isSelected ? 'primary' : 'neutral'}
                            sx={{ fontSize: '10px', minHeight: 18, height: 18 }}>
                            {cat.question_count}
                        </Chip>
                    </Box>
                );
            })}
        </Box>
    );
}


/* ═══════════════════════════════════════
   Mass Generator — sub-components
   ═══════════════════════════════════════ */

const statusColors = {
    pending: 'warning', approved: 'success', rejected: 'danger', imported: 'primary',
    draft: 'neutral', generating: 'warning', completed: 'success', failed: 'danger',
};
const statusLabels = {
    pending: 'Ожидает', approved: 'Одобрен', rejected: 'Отклонён', imported: 'Импортирован',
    draft: 'Черновик', generating: 'Генерация...', completed: 'Завершён', failed: 'Ошибка',
};

function QuestionCard({ question, onApprove, onReject, loading }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Chip size="sm" color={statusColors[question.status]} variant="soft">
                            {statusLabels[question.status]}
                        </Chip>
                        {question.topic && (
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{question.topic}</Typography>
                        )}
                    </Stack>
                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{question.text}</Typography>
                </Box>
                {question.status === 'pending' && (
                    <Stack direction="row" spacing={0.5}>
                        <IconButton size="sm" color="success" variant="soft"
                            onClick={() => onApprove(question.id)} disabled={loading}>
                            <CheckCircle size={20} weight="fill" />
                        </IconButton>
                        <IconButton size="sm" color="danger" variant="soft"
                            onClick={() => onReject(question.id)} disabled={loading}>
                            <XCircle size={20} />
                        </IconButton>
                    </Stack>
                )}
            </Stack>
            <Button variant="plain" size="sm" color="neutral" onClick={() => setExpanded(!expanded)}
                endDecorator={expanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
                sx={{ mt: 1, px: 0 }}>
                {expanded ? 'Скрыть ответы' : 'Показать ответы'}
            </Button>
            {expanded && (
                <Box sx={{ mt: 1 }}>
                    {question.answers.map((a) => (
                        <Stack key={a.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                            <Chip size="sm" variant={a.is_correct ? 'solid' : 'outlined'}
                                color={a.is_correct ? 'success' : 'neutral'} sx={{ minWidth: 28 }}>
                                {a.letter}
                            </Chip>
                            <Typography level="body-sm">{a.text}</Typography>
                        </Stack>
                    ))}
                    {question.explanation && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                            <Typography level="body-xs" fontWeight="lg" sx={{ mb: 0.5 }}>Объяснение:</Typography>
                            <Typography level="body-xs" sx={{ whiteSpace: 'pre-wrap' }}>{question.explanation}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Sheet>
    );
}

function BatchCard({ batch, onSelect, onDelete, selected }) {
    return (
        <Card variant="outlined"
            sx={{
                cursor: 'pointer',
                borderColor: selected ? 'primary.500' : 'divider',
                borderWidth: selected ? 2 : 1,
                '&:hover': { borderColor: selected ? 'primary.500' : 'neutral.400' },
            }}
            onClick={() => onSelect(batch.id)}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography level="title-sm">{batch.title}</Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        {new Date(batch.created_at).toLocaleString('ru-RU')}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="sm" color={statusColors[batch.status]} variant="soft">
                        {statusLabels[batch.status]}
                    </Chip>
                    <IconButton size="sm" color="danger" variant="plain"
                        onClick={(e) => { e.stopPropagation(); onDelete(batch.id); }}>
                        <Trash size={16} />
                    </IconButton>
                </Stack>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Typography level="body-xs">Всего: <b>{batch.total_generated}</b></Typography>
                <Typography level="body-xs" color="warning">Ожидает: <b>{batch.pending_count}</b></Typography>
                <Typography level="body-xs" color="success">Одобрено: <b>{batch.approved_count}</b></Typography>
                <Typography level="body-xs" color="danger">Отклонено: <b>{batch.rejected_count}</b></Typography>
                <Typography level="body-xs" color="primary">Импорт: <b>{batch.imported_count}</b></Typography>
            </Stack>
        </Card>
    );
}


/* ═══════════════════════════════════════
   Generated Questions Tab content
   ═══════════════════════════════════════ */

function GeneratedQuestionsTab({ token }) {
    const [batches, setBatches] = useState([]);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [batchDetail, setBatchDetail] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadBatches = async () => {
        try { setLoading(true); const res = await APIQgenListBatches(token);
            if (res.success) setBatches(res.batches);
        } catch (e) { setError('Ошибка загрузки батчей'); } finally { setLoading(false); }
    };
    const loadStats = async () => {
        try { const res = await APIQgenStats(token); if (res.success) setStats(res); } catch (e) {}
    };
    const loadBatchDetail = async (batchId) => {
        try { setLoading(true); const res = await APIQgenBatchDetail(token, batchId);
            if (res.success) setBatchDetail(res.batch);
        } catch (e) { setError('Ошибка загрузки деталей'); } finally { setLoading(false); }
    };

    useEffect(() => { if (token) { loadBatches(); loadStats(); } }, [token]);
    useEffect(() => { if (selectedBatchId && token) loadBatchDetail(selectedBatchId); }, [selectedBatchId]);

    const handleSelectBatch = (id) => { setSelectedBatchId(id); setBatchDetail(null); };
    const handleDeleteBatch = async (id) => {
        if (!window.confirm('Удалить батч?')) return;
        try { await APIQgenDeleteBatch(token, id);
            setBatches(batches.filter(b => b.id !== id));
            if (selectedBatchId === id) { setSelectedBatchId(null); setBatchDetail(null); }
            loadStats();
        } catch (e) { setError('Ошибка удаления'); }
    };
    const handleApprove = async (qId) => {
        try { setActionLoading(true); await APIQgenApproveQuestion(token, qId);
            loadBatchDetail(selectedBatchId); loadBatches(); loadStats();
        } catch (e) { setError('Ошибка одобрения'); } finally { setActionLoading(false); }
    };
    const handleReject = async (qId) => {
        try { setActionLoading(true); await APIQgenRejectQuestion(token, qId);
            loadBatchDetail(selectedBatchId); loadBatches(); loadStats();
        } catch (e) { setError('Ошибка отклонения'); } finally { setActionLoading(false); }
    };
    const handleApproveAll = async () => {
        if (!window.confirm('Одобрить все ожидающие вопросы?')) return;
        try { setActionLoading(true); await APIQgenApproveAll(token, selectedBatchId);
            loadBatchDetail(selectedBatchId); loadBatches(); loadStats();
        } catch (e) { setError('Ошибка массового одобрения'); } finally { setActionLoading(false); }
    };
    const handleImport = async () => {
        if (!window.confirm('Импортировать одобренные вопросы в основной банк?')) return;
        try { setActionLoading(true); const res = await APIQgenImport(token, selectedBatchId);
            if (res.success) { alert(`Импортировано: ${res.imported_count} вопросов`);
                loadBatchDetail(selectedBatchId); loadBatches(); loadStats(); }
        } catch (e) { setError('Ошибка импорта'); } finally { setActionLoading(false); }
    };

    const pendingCount = batchDetail?.questions?.filter(q => q.status === 'pending').length || 0;
    const approvedCount = batchDetail?.questions?.filter(q => q.status === 'approved').length || 0;

    return (
        <Box>
            {error && (
                <Alert color="danger" sx={{ mb: 2 }} endDecorator={
                    <Button size="sm" variant="plain" color="danger" onClick={() => setError(null)}>OK</Button>
                }>{error}</Alert>
            )}

            {/* Stats strip */}
            {stats && (
                <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                    <Sheet variant="soft" sx={{ px: 2, py: 1, borderRadius: 'md' }}>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Батчей</Typography>
                        <Typography level="title-md">{stats.total_batches}</Typography>
                    </Sheet>
                    <Sheet variant="soft" sx={{ px: 2, py: 1, borderRadius: 'md' }}>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Сгенерировано</Typography>
                        <Typography level="title-md">{stats.total_generated}</Typography>
                    </Sheet>
                    <Sheet variant="soft" sx={{ px: 2, py: 1, borderRadius: 'md', bgcolor: 'success.softBg' }}>
                        <Typography level="body-xs" color="success">Одобрено</Typography>
                        <Typography level="title-md" color="success">{stats.total_approved}</Typography>
                    </Sheet>
                    <Sheet variant="soft" sx={{ px: 2, py: 1, borderRadius: 'md', bgcolor: 'danger.softBg' }}>
                        <Typography level="body-xs" color="danger">Отклонено</Typography>
                        <Typography level="title-md" color="danger">{stats.total_rejected}</Typography>
                    </Sheet>
                    <Sheet variant="soft" sx={{ px: 2, py: 1, borderRadius: 'md' }}>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Импортировано</Typography>
                        <Typography level="title-md">{stats.total_imported}</Typography>
                    </Sheet>
                </Stack>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                {/* Left: batch list */}
                <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography level="title-sm">Батчи генерации</Typography>
                        <IconButton variant="outlined" size="sm" color="neutral"
                            onClick={() => { loadBatches(); loadStats(); }}>
                            <ArrowClockwise size={16} />
                        </IconButton>
                    </Stack>
                    {loading && !batches.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size="sm" /></Box>
                    ) : batches.length === 0 ? (
                        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                Нет батчей. Запустите генерацию через скрипт.
                            </Typography>
                        </Sheet>
                    ) : (
                        <Stack spacing={1.5}>
                            {batches.map(b => (
                                <BatchCard key={b.id} batch={b} onSelect={handleSelectBatch}
                                    onDelete={handleDeleteBatch} selected={selectedBatchId === b.id} />
                            ))}
                        </Stack>
                    )}
                </Box>

                {/* Right: batch detail */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {!selectedBatchId ? (
                        <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                            <Typography level="body-md" sx={{ color: 'text.tertiary' }}>
                                Выберите батч слева для просмотра вопросов
                            </Typography>
                        </Sheet>
                    ) : loading && !batchDetail ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : batchDetail ? (
                        <Box>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Box>
                                    <Typography level="title-md">{batchDetail.title}</Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        {batchDetail.questions?.length || 0} вопросов | Модель: {batchDetail.model_name}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    {pendingCount > 0 && (
                                        <Button size="sm" color="success" variant="soft"
                                            startDecorator={<Checks size={18} />}
                                            onClick={handleApproveAll} disabled={actionLoading}>
                                            Одобрить все ({pendingCount})
                                        </Button>
                                    )}
                                    {approvedCount > 0 && (
                                        <Button size="sm" color="primary" variant="solid"
                                            startDecorator={<DownloadSimple size={18} />}
                                            onClick={handleImport} disabled={actionLoading}>
                                            Импорт ({approvedCount})
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>

                            {batchDetail.error_log && (
                                <Alert color="warning" sx={{ mb: 2 }}>
                                    <Typography level="body-xs">{batchDetail.error_log}</Typography>
                                </Alert>
                            )}

                            <Tabs defaultValue="all" sx={{ borderRadius: 'md' }}>
                                <TabList>
                                    <Tab value="all">Все ({batchDetail.questions?.length || 0})</Tab>
                                    <Tab value="pending">Ожидают ({batchDetail.pending_count})</Tab>
                                    <Tab value="approved">Одобрены ({batchDetail.approved_count})</Tab>
                                    <Tab value="rejected">Отклонены ({batchDetail.rejected_count})</Tab>
                                </TabList>
                                {['all', 'pending', 'approved', 'rejected'].map(tab => (
                                    <TabPanel key={tab} value={tab} sx={{ p: 0, pt: 2 }}>
                                        {(batchDetail.questions || [])
                                            .filter(q => tab === 'all' || q.status === tab)
                                            .map(q => (
                                                <QuestionCard key={q.id} question={q}
                                                    onApprove={handleApprove} onReject={handleReject}
                                                    loading={actionLoading} />
                                            ))
                                        }
                                        {(batchDetail.questions || []).filter(q => tab === 'all' || q.status === tab).length === 0 && (
                                            <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center', py: 3 }}>
                                                Нет вопросов
                                            </Typography>
                                        )}
                                    </TabPanel>
                                ))}
                            </Tabs>
                        </Box>
                    ) : null}
                </Box>
            </Stack>
        </Box>
    );
}


/* ═══════════════════════════════════════
   Main TasksPage with two tabs
   ═══════════════════════════════════════ */

export default function TasksPage() {
    const { token, user } = useContext(LayoutContext);
    const navigate = useNavigate();

    const [categories, setCategories] = useState([]);
    const [tree, setTree] = useState([]);
    const [loadingCats, setLoadingCats] = useState(true);
    const [selectedCats, setSelectedCats] = useState([]);
    const [numQuestions, setNumQuestions] = useState(10);
    const [starting, setStarting] = useState(false);

    // Admin modal
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCase, setEditingCase] = useState(null);

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoadingCats(true);
        try {
            const res = await APIGetClinicalCategories(token);
            if (res.success) {
                setCategories(res.categories || []);
                setTree(res.tree || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingCats(false);
        }
    };

    const totalAvailable = selectedCats.length === 0
        ? categories.reduce((s, c) => s + c.question_count, 0)
        : categories.filter(c => selectedCats.includes(c.id)).reduce((s, c) => s + c.question_count, 0);

    const toggleCat = (id) => {
        setSelectedCats(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleGroup = (ids) => {
        setSelectedCats(prev => {
            const allSelected = ids.every(id => prev.includes(id));
            if (allSelected) return prev.filter(id => !ids.includes(id));
            return [...new Set([...prev, ...ids])];
        });
    };

    const allCatIds = categories.map(c => c.id);
    const isAllSelected = allCatIds.length > 0 && allCatIds.every(id => selectedCats.includes(id));

    const toggleAll = () => {
        if (isAllSelected) setSelectedCats([]);
        else setSelectedCats(allCatIds);
    };

    const handleStart = async () => {
        setStarting(true);
        try {
            const res = await APIGenerateClinicalTest(token, numQuestions, selectedCats);
            if (res.success) navigate(`/test-session/${res.test.id}`);
            else alert(res.error || 'Ошибка');
        } catch (e) {
            console.error(e);
            alert('Ошибка при создании теста');
        } finally {
            setStarting(false);
        }
    };

    return (
        <Box sx={{ px: { xs: 2, md: 7 }, pt: 5, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography level="h2">Задачи</Typography>

            <Tabs defaultValue="clinical" sx={{ borderRadius: 'md' }}>
                <TabList>
                    <Tab value="clinical">Клинические задачи</Tab>
                    {user?.role === 'admin' && <Tab value="generated">Сгенерированные</Tab>}
                </TabList>

                {/* Tab 1: Clinical Cases */}
                <TabPanel value="clinical" sx={{ p: 0, pt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        {user?.role === 'admin' && (
                            <Button startDecorator={<Plus size={18} />}
                                onClick={() => { setEditingCase(null); setModalOpen(true); }}
                                size="sm" variant="outlined">
                                Создать
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* Left: category tree */}
                        <Sheet variant="outlined" sx={{
                            flex: 2, p: 2, borderRadius: 'lg',
                            maxHeight: 'calc(100vh - 280px)', overflow: 'hidden',
                            display: 'flex', flexDirection: 'column',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography level="title-md">Категории</Typography>
                                <Checkbox checked={isAllSelected}
                                    indeterminate={selectedCats.length > 0 && !isAllSelected}
                                    onChange={toggleAll} label="ВСЕ" size="sm"
                                    sx={{ fontWeight: 600, '& .MuiCheckbox-label': { fontSize: '0.8rem' } }} />
                            </Box>
                            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                                {loadingCats ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                                ) : tree.length === 0 ? (
                                    <Typography color="neutral" sx={{ textAlign: 'center', py: 4 }}>
                                        Нет доступных категорий
                                    </Typography>
                                ) : (
                                    tree.map(system => (
                                        <SystemNode key={system.name} system={system}
                                            selectedCats={selectedCats} onToggleCat={toggleCat}
                                            onToggleSystem={toggleGroup} />
                                    ))
                                )}
                            </Box>
                            {selectedCats.length > 0 && (
                                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography level="body-sm" color="primary">
                                        Выбрано: {selectedCats.length} из {categories.length} категорий
                                    </Typography>
                                </Box>
                            )}
                        </Sheet>

                        {/* Right: settings + start */}
                        <Sheet variant="outlined" sx={{ flex: 1, p: 3, borderRadius: 'lg', alignSelf: 'flex-start' }}>
                            <Typography level="title-md" sx={{ mb: 3 }}>Настройки</Typography>
                            <Stack spacing={2}>
                                <Card variant="soft" sx={{ p: 2 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                        <Hash size={20} />
                                        <Typography level="title-sm" fontWeight={600}>Количество задач</Typography>
                                        <Chip size="sm" color="primary" variant="solid">{numQuestions}</Chip>
                                    </Stack>
                                    <Typography level="body-xs" sx={{ mb: 1.5, color: 'text.secondary' }}>
                                        Доступно: <b>{totalAvailable}</b>
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                        {[5, 10, 20, 40].map(n => (
                                            <Button key={n} size="sm"
                                                variant={numQuestions === n ? 'solid' : 'outlined'}
                                                color={numQuestions === n ? 'primary' : 'neutral'}
                                                onClick={() => setNumQuestions(n)} sx={{ minWidth: 44 }}>
                                                {n}
                                            </Button>
                                        ))}
                                    </Stack>
                                    <Input type="number" size="sm" value={numQuestions}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            if (!isNaN(v) && v >= 1 && v <= 168) setNumQuestions(v);
                                        }}
                                        slotProps={{ input: { min: 1, max: 168, style: { textAlign: 'center' } } }}
                                        sx={{ bgcolor: 'background.surface' }} />
                                </Card>
                                <Divider sx={{ my: 1 }} />
                                <Button size="lg" startDecorator={<Play size={18} />}
                                    onClick={handleStart} loading={starting} fullWidth
                                    sx={{
                                        py: 1.5, fontSize: 'md', fontWeight: 600,
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#37352F',
                                        color: (theme) => theme.palette.mode === 'dark' ? '#1F1F1F' : '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E5E5E7' : '#4A4A45',
                                        },
                                    }}>
                                    Решать
                                </Button>
                            </Stack>
                        </Sheet>
                    </Box>
                </TabPanel>

                {/* Tab 2: Generated Questions */}
                {user?.role === 'admin' && (
                    <TabPanel value="generated" sx={{ p: 0, pt: 3 }}>
                        <GeneratedQuestionsTab token={token} />
                    </TabPanel>
                )}
            </Tabs>

            <ClinicalCaseModal
                open={modalOpen}
                onClose={(refresh) => {
                    setModalOpen(false);
                    setEditingCase(null);
                    if (refresh) loadCategories();
                }}
                editingCase={editingCase}
            />
        </Box>
    );
}
